import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject, takeUntil, delay, timer } from 'rxjs';
import { KontrollterminService } from '../../services/kontrolltermin.service';
import { Kontrolltermin, KontrollIndikator, ArtikelItem } from '../../services/kontrolltermin.service';
import { ReportService, ReportInfo, ReportStatus, Report, ReportCategory, ReportCategoryOption } from '../../services/report.service';
import { DokumenteService, FileUpload } from '../../services/dokumente.service';
import { JournalService } from '../../@core/journal.service';
import { environment } from '../../../environments/environment';
import { getSession } from '../../auth/storage';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AuthQuery } from '@app/auth/auth.query';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DialogIndikatorEditComponent } from '../dialogs/dialog-indikator-edit/dialog-indikator-edit.component';
import { EmailPreviewDialogComponent } from '../dialogs/email-preview-dialog/email-preview-dialog.component';
import { ImageModalComponent, ImageModalData } from '../dialogs/image-modal/image-modal.component';
import { EingesetzteProdukteDialogComponent } from '../dialogs/eingesetzte-produkte-dialog/eingesetzte-produkte-dialog.component';
import { ReportCategorySelectionDialogComponent } from '../dialogs/report-category-selection-dialog/report-category-selection-dialog.component';
import { KontrollIndikatorService } from '../../services/kontroll-indikator.service';
import { TrendChartService } from '../../services/trend-chart.service';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { StammdatenService, MobileTextTemplate } from '@app/services/stammdaten.service';

interface ReportContact {
  email: string;
  name: string;
  anrede: string;
  selected?: boolean;
}

@Component({
  selector: 'app-kontrolltermin-edit',
  templateUrl: './kontrolltermin-edit.component.html',
  styleUrls: ['./kontrolltermin-edit.component.scss']
})
export class KontrollterminEditComponent implements OnInit, OnDestroy {
  kontrolltermin: Kontrolltermin | null = null;
  kontrollterminId: string = '';
  isLoading = true;
  isEditMode = false;
  isFreigabeEditMode = false; // Edit mode for freigabe view
  isSaving = false;
  berichtEmail: string = '';
  
  // Email sending interface
  savedContacts: ReportContact[] = [];
  additionalContact: ReportContact = { email: '', name: '', anrede: '' };
  selectedRecipients: string[] = [];
  isAdditionalContactExpanded = false;
  emailMessage = ''; // TinyMCE editor content
  emailSubject = ''; // Email subject
  tinymceInit: any; // TinyMCE configuration
  
  // Report tracking
  reportInfo: ReportInfo | null = null;
  reportStatus: ReportStatus | null = null;
  hasReportGenerated = false;
  hasReportSent = false;
  isGeneratingReport = false;
  isSendingReport = false;
  reportVersions: Report[] = []; // All report versions for this kontrolltermin
  selectedReportCategory: ReportCategory = 'standard'; // Selected report category for generation
  availableReportCategories: ReportCategoryOption[] = []; // Available report categories
  isCategoryDropdownOpen = false; // Track dropdown open state

  // Trend analysis sub-report selection
  trendReportType: string = 'auswertung_trend';
  trendSelectedYear: number = new Date().getFullYear();
  trendYearOptions: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 2025; y <= currentYear; y++) {
      years.push(y);
    }
    return years;
  })();
  trendDateFrom: string = new Date().getFullYear() + '-01-01';
  trendDateTo: string = new Date().getFullYear() + '-12-31';
  trendReportTypes = [
    { value: 'auswertung_trend', label: 'Auswertung Trend' },
    { value: 'trend_matrix', label: 'Auswertung Trend: Matrix' },
    { value: 'periodenbericht', label: 'Periodenbericht' }
  ];
  
  // Status tracking
  sourceStatus: string = ''; // Track source status from tab
  currentStatus: string = ''; // Track kontrolltermin's current status
  
  // Form
  kontrollterminForm: FormGroup;

  // Mobile text templates (Textbausteine) for quick-inserting into Anmerkungen
  textTemplates: MobileTextTemplate[] = [];
  selectedTemplateId: number | null = null;
  
  // Dropdown options
  einsatztypOptions = environment.kontrolltermin_einsatztyp;
  statusOptions = environment.kontrolltermin_status;
  befallAmpelOptions = environment.kontroll_indikator_befall_ampel;
  
  // Weekday options
  wochentagOptions = [
    { value: 'Montag', label: 'Montag' },
    { value: 'Dienstag', label: 'Dienstag' },
    { value: 'Mittwoch', label: 'Mittwoch' },
    { value: 'Donnerstag', label: 'Donnerstag' },
    { value: 'Freitag', label: 'Freitag' },
    { value: 'Samstag', label: 'Samstag' },
    { value: 'Sonntag', label: 'Sonntag' }
  ];
  
  // Month options
  monatOptions = [
    { value: 'Januar 2024', label: 'Januar 2024' },
    { value: 'Februar 2024', label: 'Februar 2024' },
    { value: 'März 2024', label: 'März 2024' },
    { value: 'April 2024', label: 'April 2024' },
    { value: 'Mai 2024', label: 'Mai 2024' },
    { value: 'Juni 2024', label: 'Juni 2024' },
    { value: 'Juli 2024', label: 'Juli 2024' },
    { value: 'August 2024', label: 'August 2024' },
    { value: 'September 2024', label: 'September 2024' },
    { value: 'Oktober 2024', label: 'Oktober 2024' },
    { value: 'November 2024', label: 'November 2024' },
    { value: 'Dezember 2024', label: 'Dezember 2024' }
  ];
  
  private destroy$ = new Subject<void>();
  
  // Image storage per indikator
  indikatorImages: { [indikatorId: string]: FileUpload[] } = {};
  imageUrls: { [imageId: number]: string } = {}; // Cache for signed URLs
  private _pendingUrlRequests?: { [id: number]: boolean }; // Track pending URL requests
  private _loadingImagesFor: { [indikatorId: string]: boolean } = {}; // Track which indikatoren are loading images

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private kontrollterminService: KontrollterminService,
    private kontrollIndikatorService: KontrollIndikatorService,
    private reportService: ReportService,
    private dokumenteService: DokumenteService,
    private journalService: JournalService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    private authQuery: AuthQuery,
    private http: HttpClient,
    private trendChartService: TrendChartService,
    private translate: TranslateService,
    private stammdatenService: StammdatenService
  ) {
    this.kontrollterminForm = this.fb.group({});
  }

  /**
   * Load the active mobile text templates for the Anmerkungen quick-insert dropdown.
   */
  private loadTextTemplates(): void {
    this.stammdatenService.getMobileTextTemplates().subscribe({
      next: (templates) => {
        this.textTemplates = templates || [];
      },
      error: (error) => {
        console.error('Error loading text templates:', error);
      }
    });
  }

  /**
   * Append the selected template's text to the Anmerkungen field on a new line.
   */
  insertTemplateIntoAnmerkungen(): void {
    const template = this.textTemplates.find(t => t.id === this.selectedTemplateId);
    if (!template) {
      return;
    }
    const ctrl = this.kontrollterminForm.get('anmerkungen');
    const current = (ctrl?.value || '').trim();
    ctrl?.setValue(current ? `${current}\n${template.text}` : template.text);
    ctrl?.markAsDirty();
  }

  ngOnInit(): void {
    // Initialize TinyMCE configuration (same as ot-meetings project)
    // TinyMCE is located in src/assets/tinymce
    this.tinymceInit = {
      base_url: '/assets/tinymce/',
      suffix: '.min',
      height: 300,
      min_height: 300,
      max_height: 500,
      menubar: false,
      plugins: [
        'advlist',
        'autolink',
        'link',
        'image',
        'lists',
        'charmap',
        'searchreplace',
        'visualblocks',
        'fullscreen',
        'table',
        'code',
        'wordcount',
        'autoresize',
      ],
      autoresize_min_height: 300,
      autoresize_max_height: 500,
      autoresize_on_init: true,
      autoresize_bottom_margin: 10,
      toolbar:
        'undo redo | formatselect | bold italic backcolor | \
      alignleft aligncenter alignright alignjustify | \
      bullist numlist outdent indent | table | searchreplace removeformat | fullscreen visualblocks code',
      // language: 'de', // Commented out - language file not found
      // Allow TinyMCE to use <p> tags but convert newlines properly
      forced_root_block: 'p',
      force_br_newlines: false,
      convert_newlines_to_brs: false,
      // This will preserve line breaks when content is loaded
      paste_as_text: false,
      paste_auto_cleanup_on_paste: true
    };

    // Load Textbausteine for the Anmerkungen quick-insert dropdown
    this.loadTextTemplates();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.kontrollterminId = params['id'];
      if (this.kontrollterminId) {
        this.loadKontrolltermin();
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const previousSourceStatus = this.sourceStatus;
      this.isEditMode = params['mode'] === 'edit';
      this.sourceStatus = params['sourceStatus'] || 'unknown';
      
      // If navigating to abgeschlossen view (especially via redirect), reload everything
      if (this.sourceStatus === 'abgeschlossen' && 
          previousSourceStatus !== 'abgeschlossen' && 
          this.kontrollterminId &&
          this.kontrolltermin) {
        console.log('🔄 Reloading kontrolltermin for abgeschlossen view (redirect detected)...');
        // Full reload to ensure everything is fresh
        this.loadKontrolltermin();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadKontrolltermin(): void {
    this.isLoading = true;
    
    // Validate kontrollterminId before making the call
    if (!this.kontrollterminId) {
      console.error('❌ Cannot load kontrolltermin: kontrollterminId is empty');
      this.isLoading = false;
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_ID_MISSING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      this.router.navigate(['/kontrolltermine']);
      return;
    }
    
    
    // Load kontrolltermin and related data
    this.kontrollterminService.getKontrolltermin(this.kontrollterminId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (kontrolltermin) => {
        // CRITICAL: Check if kontrolltermin is null or undefined
        if (!kontrolltermin) {
          console.error('❌ Kontrolltermin response is null or undefined');
          this.isLoading = false;
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NOT_FOUND'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
          setTimeout(() => {
            this.router.navigate(['/kontrolltermine']);
          }, 2000);
          return;
        }
        
        // Additional validation: check if it's an object with expected properties
        if (typeof kontrolltermin !== 'object' || !kontrolltermin.hasOwnProperty('id')) {
          console.error('❌ Invalid kontrolltermin response structure:', kontrolltermin);
          this.isLoading = false;
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_INVALID_DATA'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
          return;
        }
        
        this.kontrolltermin = kontrolltermin;
        this.currentStatus = this.getStatusKeyFromId(kontrolltermin.status_id);
        
        // DEBUG: Log freigabe data
        console.log('🔍 KontrollterminEditComponent - DEBUG freigabe_durch_name:');
        console.log('  - kontrolltermin.freigabe exists:', !!kontrolltermin.freigabe);
        if (kontrolltermin.freigabe) {
          console.log('  - freigabe.freigabe_durch:', kontrolltermin.freigabe.freigabe_durch);
          console.log('  - freigabe.freigabe_durch_name:', kontrolltermin.freigabe.freigabe_durch_name);
          console.log('  - freigabe.freigabeDurch exists:', !!kontrolltermin.freigabe.freigabeDurch);
          if (kontrolltermin.freigabe.freigabeDurch) {
            console.log('  - freigabeDurch.id:', kontrolltermin.freigabe.freigabeDurch.id);
            console.log('  - freigabeDurch.full_name:', kontrolltermin.freigabe.freigabeDurch.full_name);
          }
          console.log('  - Full freigabe object:', JSON.stringify(kontrolltermin.freigabe, null, 2));
        }
        
        // Update page title with kontrolltermin number
        this.updatePageTitle();
        
        // Handle both camelCase and snake_case naming conventions
        const kontrollIndikatoren = kontrolltermin.kontrollIndikatoren || kontrolltermin.kontroll_indikatoren;
        
        // Normalize the naming convention for consistency
        if (kontrolltermin.kontroll_indikatoren && !kontrolltermin.kontrollIndikatoren) {
          kontrolltermin.kontrollIndikatoren = kontrolltermin.kontroll_indikatoren;
        }
        
        // Sort kontrollIndikatoren by indikator_number
        if (kontrolltermin.kontrollIndikatoren && Array.isArray(kontrolltermin.kontrollIndikatoren)) {
          kontrolltermin.kontrollIndikatoren = this.sortIndicatorsByNumber(kontrolltermin.kontrollIndikatoren);
        }
        
        // Initialize bericht email with contact email
        this.berichtEmail = this.getContactEmail();
        
        // Initialize report categories
        this.availableReportCategories = this.reportService.getAvailableReportCategories();
        
        // Load saved contacts for email sending interface
        this.loadSavedContacts();
        
        // Check report status for abgeschlossen status
        if (this.currentStatus === 'abgeschlossen') {
          this.checkReportStatus();
          this.loadReportVersions(); // Load all report versions
          this.loadEmailTemplate(); // Load email template to prefill message
        }
        
        this.setupStatusSpecificBehavior();
        
        
        // KontrollIndikatoren are loaded with the kontrolltermin via the main API
        
        // Don't load images immediately - load them lazily when needed
        // Images will be loaded on-demand when getImagesForIndikator is called
        
        // Check if freigabe is already loaded from the main API call
        if (this.kontrolltermin!.status_id === 7 || this.kontrolltermin!.status_id === 8) {
          if (this.kontrolltermin!.freigabe) {
            // Freigabe already loaded from main API call
            console.log('🔍 Freigabe already loaded from main API call');
            console.log('  - freigabe_durch_name:', this.kontrolltermin!.freigabe.freigabe_durch_name);
            this.initializeForm();
            this.isLoading = false;
          } else {
            // Load freigabe separately if not included in main call
            console.log('🔍 Loading freigabe separately...');
            this.kontrollterminService.getFreigabeByKontrolltermin(this.kontrollterminId).pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (freigabe) => {
                console.log('🔍 Freigabe loaded separately:', freigabe);
                console.log('  - freigabe_durch:', freigabe?.freigabe_durch);
                console.log('  - freigabe_durch_name:', freigabe?.freigabe_durch_name);
                this.kontrolltermin!.freigabe = freigabe || undefined; // Convert null to undefined for TypeScript
                this.initializeForm();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error loading freigabe:', error);
                // No freigabe found, which is fine
                this.kontrolltermin!.freigabe = undefined;
                this.initializeForm();
                this.isLoading = false;
              }
            });
          }
        } else {
          // For other statuses (like geplant), skip freigabe loading
          this.initializeForm();
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('❌ Error loading kontrolltermin:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);
        this.isLoading = false;
        
        // Check if it's a 404 or "not found" error
        if (error.status === 404 || error.status === 500) {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NOT_FOUND'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
          // Navigate back to kontrolltermine list after a short delay
          setTimeout(() => {
            this.router.navigate(['/kontrolltermine']);
          }, 2000);
        } else {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_LOADING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      }
    });
  }

  initializeForm(): void {
    if (!this.kontrolltermin) return;

    this.kontrollterminForm = this.fb.group({
      // Section A: Basic Data
      einsatznummer: [{ value: this.kontrolltermin.einsatznummer, disabled: true }],
      standort_id: [this.kontrolltermin.standort_id, Validators.required],
      projektaufgabe_id: [this.kontrolltermin.projektaufgabe_id, Validators.required],
      kunde_id: [this.kontrolltermin.kunde_id, Validators.required],
      techniker_id: [this.kontrolltermin.techniker_id, Validators.required],
      einsatztyp: [this.kontrolltermin.einsatztyp, Validators.required],
      status_id: [this.kontrolltermin.status_id, Validators.required],
      datum_durchfuerung: [this.kontrolltermin.datum_durchfuerung, Validators.required],
      monat_plan: [this.kontrolltermin.monat_plan],
      wochentag_plan: [this.kontrolltermin.wochentag_plan],
      soll_zeit: [this.kontrolltermin.soll_zeit],
      ist_zeit: [this.kontrolltermin.ist_zeit],
      verrechnete_viertel: [this.kontrolltermin.verrechnete_viertel],
      anmerkungen: [this.kontrolltermin.anmerkungen],
      zusatz_massnahme: [this.kontrolltermin.zusatz_massnahme],
      
      // Section C: Freigabe Data
      freigabe: this.fb.group({
        freigegeben: [this.kontrolltermin.freigabe?.freigegeben || false],
        freigabe_datum: [this.kontrolltermin.freigabe?.freigabe_datum || null],
        freigabe_durch: [this.kontrolltermin.freigabe?.freigabe_durch || null],
        anmerkungen_freigabe: [this.kontrolltermin.freigabe?.anmerkungen_freigabe || ''],
        versand_datum: [this.kontrolltermin.freigabe?.versand_datum || null],
        auswertung_link: [this.kontrolltermin.freigabe?.auswertung_link || ''],
        bericht_link: [this.kontrolltermin.freigabe?.bericht_link || '']
      }),
      
      // Section B: Indicators (will be handled separately)
              kontrollIndikatoren: this.fb.array([])
    });

    // Initialize indicators form array
    if (this.kontrolltermin.kontrollIndikatoren) {
      this.initializeIndicatorsForm();
    }
  }

  initializeIndicatorsForm(): void {
    const indicatorsArray = this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
    indicatorsArray.clear();

    this.kontrolltermin?.kontrollIndikatoren?.forEach(indikator => {
      indicatorsArray.push(this.fb.group({
        id: [indikator.id],
        gebaeude_id: [indikator.gebaeude_id],
        gebaeude_name: [indikator.gebaeude_name],
        sektion_id: [indikator.sektion_id],
        sektion_name: [indikator.sektion_name],
        room_id: [indikator.room_id],
        room_name: [indikator.room_name],
        indikator_id: [indikator.indikator_id],
        indikator_name: [indikator.indikator_name],
        wert: [indikator.wert],
        befall: [indikator.befall],
        befall_status_id: [indikator.befall_status_id],
        problem: [indikator.problem],
        massnahme: [indikator.massnahme],
        checked: [indikator.checked],
        checked_by: [indikator.checked_by],
        anmerkung: [indikator.anmerkung],
        interne_notiz: [indikator.interne_notiz]
      }));
    });
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    this.updateUrl();
  }

  updateUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: this.isEditMode ? 'edit' : 'view' },
      queryParamsHandling: 'merge'
    });
  }

  saveKontrolltermin(): void {
    if (!this.kontrolltermin || !this.kontrollterminForm.valid) return;

    this.isSaving = true;
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get the current kontrollIndikatoren (for preserving in local state after save)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Merge form data with existing kontrolltermin
    // CRITICAL: Do NOT include kontrollIndikatoren in the payload - they should only be updated
    // when explicitly modifying indikatoren, not when saving "Zusätzliche Informationen"
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue
    };
    
    // Explicitly remove kontrollIndikatoren from payload to prevent API from recreating them
    delete updatedKontrolltermin.kontrollIndikatoren;
    delete updatedKontrolltermin.kontroll_indikatoren;

    this.kontrollterminService.updateKontrolltermin(this.kontrollterminId, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedKontrolltermin) => {
        // Log journal entry with entity IDs
        const entityIds = this.journalService.extractEntityIds(updatedKontrolltermin);
        this.journalService.log(
          'Kontrolltermin aktualisiert',
          '',
          `Kontrolltermin: ${updatedKontrolltermin?.einsatznummer || 'Unbekannt'}`,
          entityIds
        );
        
        this.isSaving = false;
        this.isEditMode = false;
        this.updateUrl();
        
        // Update local data with the response instead of reloading
        this.kontrolltermin = {
          ...updatedKontrolltermin,
          // Preserve existing kontrollIndikatoren if not in response
          kontrollIndikatoren: updatedKontrolltermin.kontrollIndikatoren || currentKontrollIndikatoren
        };
        
        // Update the form with the new data
        this.kontrollterminForm.patchValue(updatedKontrolltermin);
        
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.isSaving = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SAVING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.updateUrl();
    this.loadKontrolltermin(); // Reload to reset any changes
  }

  // Freigabe edit mode methods
  toggleFreigabeEditMode(): void {
    this.isFreigabeEditMode = !this.isFreigabeEditMode;
  }

  saveFreigabeEdits(): void {
    if (!this.kontrolltermin || !this.kontrollterminForm.valid) return;

    this.isSaving = true;
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get the current kontrollIndikatoren (for preserving in local state after save)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Merge form data with existing kontrolltermin
    // CRITICAL: Do NOT include kontrollIndikatoren in the payload - they should only be updated
    // when explicitly modifying indikatoren, not when saving "Zusätzliche Informationen"
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue
    };
    
    // Explicitly remove kontrollIndikatoren from payload to prevent API from recreating them
    delete updatedKontrolltermin.kontrollIndikatoren;
    delete updatedKontrolltermin.kontroll_indikatoren;
    
    // Update kontrolltermin with edited values
    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedKontrolltermin) => {
        this.isSaving = false;
        this.isFreigabeEditMode = false;
        // Preserve kontrollIndikatoren in the response
        this.kontrolltermin = {
          ...updatedKontrolltermin,
          kontrollIndikatoren: currentKontrollIndikatoren
        };
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_CHANGES_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.isSaving = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_CHANGES_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  cancelFreigabeEdit(): void {
    this.isFreigabeEditMode = false;
    // Reset form to original values without reloading from server
    this.initializeForm();
  }

  goBack(): void {
    this.router.navigate(['/kontrolltermine']);
  }

  getStatusHeaderColor(): string {
    const colorMap: { [key: string]: string } = {
      'geplant': '#6c757d',
      'aktuell': '#5a7d9a',
      'bearbeitung': '#f1c40f',
      'freigabe': '#e67e22',
      'abgeschlossen': '#4caf50',
      'geloescht': '#dc3545'
    };
    return colorMap[this.sourceStatus] || '#6c757d';
  }

  getStatusHeaderLabel(): string {
    const labelMap: { [key: string]: string } = {
      'geplant': 'Geplant',
      'aktuell': 'Aktuell',
      'bearbeitung': 'In Bearbeitung',
      'freigabe': 'Freigabe',
      'abgeschlossen': 'Abgeschlossen',
      'geloescht': 'Gelöscht'
    };
    return labelMap[this.sourceStatus] || 'Unbekannt';
  }

  /**
   * Navigate to abgeschlossen view of the same kontrolltermin
   */
  navigateToAbgeschlossen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }
    
    // Navigate with query params - the queryParams subscription will detect the change
    // and trigger a full reload
    this.router.navigate(['/kontrolltermine', this.kontrolltermin.id], {
      queryParams: {
        mode: 'view',
        sourceStatus: 'abgeschlossen',
        reload: Date.now() // Add timestamp to ensure query params are seen as "changed"
      },
      queryParamsHandling: 'merge'
    });
  }

  // Action Methods
  freigeben(): void {
    if (confirm('Möchten Sie diesen Kontrolltermin freigeben?')) {
      // TODO: Implement freigabe logic
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.FREIGABE_PROCESSING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
    }
  }

  alsMailSenden(): void {
    if (confirm('Möchten Sie den Bericht als E-Mail senden?')) {
      // TODO: Implement email sending logic
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.EMAIL_SENDING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
    }
  }

  berichtErstellen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isSaving = true;
    
    // Generate PDF report
    this.kontrollterminService.generatePDFReport(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        
        // Create blob and download
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generate nice filename similar to monitoring app
        const standortName = this.kontrolltermin?.standort?.name || 'Standort';
        const sanitizedStandortName = standortName
          .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '')
          .replace(/\s+/g, '-')
          .substring(0, 50);
        
        const now = new Date();
        const year = now.getFullYear();
        const germanMonths = [
          'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
          'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
        ];
        const month = germanMonths[now.getMonth()];
        
        link.download = `${sanitizedStandortName}-SBK1-Monitoring-${year}-${month}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_PDF_GENERATED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error generating PDF report:', error);
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_PDF_GENERATION'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  /**
   * Delete kontrolltermin (mark as gelöscht)
   */
  deleteKontrolltermin(): void {
    if (!this.kontrolltermin) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolltermin löschen',
        message: `Möchten Sie den Kontrolltermin #${this.kontrolltermin.einsatznummer} wirklich löschen?`,
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performDelete();
      }
    });
  }

  /**
   * Perform the actual delete operation
   */
  private performDelete(): void {
    if (!this.kontrolltermin) return;

    this.isSaving = true;
    
    // Update status to "gelöscht" (status_id: 1)
    const updateData = {
      status_id: 1 // gelöscht status
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updateData).subscribe({
      next: (response) => {
        // Log journal entry with entity IDs
        const entityIds = this.journalService.extractEntityIds(this.kontrolltermin);
        this.journalService.log(
          'Kontrolltermin gelöscht',
          '',
          `Kontrolltermin: ${this.kontrolltermin?.einsatznummer || 'Unbekannt'}`,
          entityIds
        );
        
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_DELETED'), this.translate.instant('COMMON.CLOSE'), { 
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        
        // Navigate back to the list
        this.router.navigate(['/kontrolltermine']);
      },
      error: (error) => {
        console.error('Error deleting kontrolltermin:', error);
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_DELETING'), this.translate.instant('COMMON.CLOSE'), { 
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isSaving = false;
      }
    });
  }

  // Helper Methods
  getBefallColor(befall: number | null): string {
    if (befall === 1) return 'text-red-600 bg-red-50';
    if (befall === 0) return 'text-green-600 bg-green-50';
    return 'text-gray-600 bg-gray-50';
  }

  getBefallText(befall: number | null): string {
    if (befall === 1) return 'Ja';
    if (befall === 0) return 'Nein';
    return 'Unbekannt';
  }

  getBefallStatusColor(statusId: number | null): string {
    const status = this.befallAmpelOptions.find(s => s.id === statusId);
    return status ? status.color : '#6c757d';
  }

  getBefallStatusText(statusId: number | null): string {
    const status = this.befallAmpelOptions.find(s => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  // New methods for status badges (OK/Warnung/Kritisch)
  getStatusBadgeClass(statusId: string | number | null): string {
    const id = String(statusId);
    if (id === '3') return 'bg-green-500'; // OK
    if (id === '2') return 'bg-orange-500'; // Warnung  
    if (id === '1') return 'bg-red-500'; // Kritisch
    return 'bg-gray-500'; // Default
  }

  getStatusBadgeText(statusId: string | number | null): string {
    const id = String(statusId);
    if (id === '3') return 'OK';
    if (id === '2') return 'Warnung';
    if (id === '1') return 'Kritisch';
    return 'Unbekannt';
  }

  // Helper method to check if indikator has been checked
  isIndikatorChecked(indikator: any): boolean {
    // An indikator is considered checked ONLY if BOTH conditions are met:
    // 1. It has a checked date (not null, not undefined, not empty string)
    // 2. It has a valid checked_by user (not null, not 0, not '0', not empty)
    const hasCheckedDate = indikator.checked && 
                          indikator.checked !== null && 
                          indikator.checked !== undefined && 
                          indikator.checked !== '' &&
                          indikator.checked !== 'null';
    
    const hasCheckedBy = indikator.checked_by && 
                        indikator.checked_by !== null && 
                        indikator.checked_by !== undefined && 
                        indikator.checked_by !== '' &&
                        indikator.checked_by !== 'null' &&
                        indikator.checked_by !== 0 &&
                        indikator.checked_by !== '0';
    
    const isChecked = hasCheckedDate && hasCheckedBy;
    
    return isChecked;
  }

  getStatusText(statusId: number | null): string {
    const status = this.statusOptions.find(s => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  getEinsatztypText(einsatztyp: string | null): string {
    const type = this.einsatztypOptions.find(t => t.value === einsatztyp);
    return type ? type.label : 'Unbekannt';
  }

  getTechnikerName(techniker: any): string {
    if (!techniker) return 'Nicht zugewiesen';
    if (typeof techniker === 'string') return techniker || 'Nicht zugewiesen';
    if (techniker.full_name) return techniker.full_name;
    if (techniker.display_name) return techniker.display_name;
    const first = techniker.vorname || techniker.first_name || '';
    const last = techniker.nachname || techniker.last_name || '';
    const composed = `${first} ${last}`.trim();
    if (composed) return composed;
    if (techniker.name) return techniker.name;
    if (techniker.nummer) return `#${techniker.nummer}`;
    return 'Nicht zugewiesen';
  }

  /**
   * Get display name for checked_by on a kontrollindikator.
   * Uses the checkedBy relationship or checked_by_name from the API response.
   */
  getCheckedByDisplayName(indikator: any): string {
    const checkedBy = indikator.checked_by;

    // Not checked
    if (!checkedBy || checkedBy === 0 || checkedBy === '0') {
      return 'Noch nicht geprüft';
    }

    // Superuser
    if (checkedBy === 999 || checkedBy === '999') {
      return 'System Administrator';
    }

    // Try checked_by_name from enriched API data
    if (indikator.checked_by_name && indikator.checked_by_name !== 'Noch nicht geprüft') {
      return indikator.checked_by_name;
    }

    // Try the checkedBy relationship object
    const rel = indikator.checkedBy || indikator.checked_by_relation;
    if (rel && typeof rel === 'object') {
      const name = rel.full_name || rel.display_name ||
                   `${rel.vorname || ''} ${rel.nachname || ''}`.trim() ||
                   rel.name;
      if (name) return name;
    }

    // Check if checked_by matches the kontrolltermin techniker
    if (this.kontrolltermin?.techniker_id && 
        String(checkedBy) === String(this.kontrolltermin.techniker_id)) {
      const techName = this.getTechnikerName(this.kontrolltermin.techniker);
      if (techName && techName !== 'Nicht zugewiesen') return techName;
    }

    return `Techniker #${checkedBy}`;
  }

  /**
   * Get freigabe durch name with debugging
   */
  getFreigabeDurchName(): string {
    const freigabe = this.kontrolltermin?.freigabe;
    if (!freigabe) {
      console.log('🔍 getFreigabeDurchName: No freigabe object');
      return 'Nicht zugewiesen';
    }

    console.log('🔍 getFreigabeDurchName - DEBUG:');
    console.log('  - freigabe_durch_name:', freigabe.freigabe_durch_name);
    console.log('  - freigabe_durch:', freigabe.freigabe_durch);
    console.log('  - freigabeDurch exists:', !!freigabe.freigabeDurch);
    if (freigabe.freigabeDurch) {
      console.log('  - freigabeDurch.id:', freigabe.freigabeDurch.id);
      console.log('  - freigabeDurch.full_name:', freigabe.freigabeDurch.full_name);
    }

    // Try freigabe_durch_name first (computed by API)
    if (freigabe.freigabe_durch_name) {
      console.log('  ✅ Using freigabe_durch_name:', freigabe.freigabe_durch_name);
      return freigabe.freigabe_durch_name;
    }

    // Fallback to relationship object
    const nameFromRelationship = this.getTechnikerName(freigabe.freigabeDurch);
    if (nameFromRelationship && nameFromRelationship !== 'Nicht zugewiesen') {
      console.log('  ✅ Using name from relationship:', nameFromRelationship);
      return nameFromRelationship;
    }

    console.log('  ⚠️ No name found, returning "Nicht zugewiesen"');
    return 'Nicht zugewiesen';
  }

  /**
   * Get status key from status ID
   */
  getStatusKeyFromId(statusId: number): string {
    const statusMap: {[key: number]: string} = {
      1: 'geloescht',
      2: 'abgebrochen', 
      3: 'fehlerhaft',
      4: 'geplant',
      5: 'aktuell',
      6: 'bearbeitung',
      7: 'freigabe',
      8: 'abgeschlossen'
    };
    return statusMap[statusId] || 'unknown';
  }

  /**
   * Update page title with kontrolltermin number
   */
  private updatePageTitle(): void {
    if (this.kontrolltermin) {
      // Use einsatznummer if available, otherwise use first 8 chars of ID
      const nummer = this.kontrolltermin.einsatznummer || 
                    this.kontrolltermin.id?.substring(0, 8) || 
                    'Unbekannt';
      
      const title = `Kontrolltermin #${nummer} - Bearbeiten`;
      this.titleService.setTitle(title);
    }
  }

  /**
   * Setup status-specific behavior
   */
  setupStatusSpecificBehavior(): void {
    // Different behavior based on source status
    switch(this.sourceStatus) {
      case 'geloescht':
        this.setupGeloeschtBehavior();
        break;
      case 'abgebrochen':
        this.setupAbgebrochenBehavior();
        break;
      case 'fehlerhaft':
        this.setupFehlerhaftBehavior();
        break;
      case 'geplant':
        this.setupGeplantBehavior();
        break;
      case 'aktuell':
        this.setupAktuellBehavior();
        break;
      case 'bearbeitung':
        this.setupBearbeitungBehavior();
        break;
      case 'freigabe':
        this.setupFreigabeBehavior();
        break;
      case 'abgeschlossen':
        this.setupAbgeschlossenBehavior();
        break;
    }
  }

  // Status-specific behavior methods
  setupGeloeschtBehavior(): void {
    // Limited editing, focus on restoration
  }

  setupAbgebrochenBehavior(): void {
    // Show cancellation details, reschedule options
  }

  setupFehlerhaftBehavior(): void {
    // Show error details, correction options
  }

  setupGeplantBehavior(): void {
    // Full editing, scheduling options
  }

  setupAktuellBehavior(): void {
    // Active work, progress tracking
  }

  setupBearbeitungBehavior(): void {
    // Work in progress, completion options
  }

  setupFreigabeBehavior(): void {
    // Approval workflow, final checks
  }

  setupAbgeschlossenBehavior(): void {
    // Read-only with reactivation options
  }

  /**
   * Handle status change from child components
   */
  onStatusChanged(event: any): void {
    console.log('🔄 Status change received:', event);
    const { newStatus, kontrolltermin } = event;
    
    // TODO: Implement status change logic
    // This would typically call the API to update the kontrolltermin status
    console.log(`📊 Changing status from ${this.currentStatus} to ${newStatus}`);
    
    // For now, just show a message
    this.snackBar.open(`${this.translate.instant('APPOINTMENT_EDIT.STATUS_CHANGING')}: ${newStatus}`, this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
  }

  /**
   * Handle header field changes
   */
  /**
   * Handle batch field changes from header edit dialog.
   * Sends ALL changed fields in a SINGLE API call.
   */
  onHeaderBatchChanged(changes: { [key: string]: any }): void {
    console.log('KontrollterminEditComponent: onHeaderBatchChanged() called with:', changes);

    if (!this.kontrolltermin?.id) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_ID_NOT_FOUND'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    if (Object.keys(changes).length === 0) return;

    this.isSaving = true;

    // Update form controls for any matching fields
    if (changes['soll_zeit'] !== undefined || changes['ist_zeit'] !== undefined) {
      const patch: any = {};
      if (changes['soll_zeit'] !== undefined) patch['soll_zeit'] = changes['soll_zeit'];
      if (changes['ist_zeit'] !== undefined) patch['ist_zeit'] = changes['ist_zeit'];
      this.kontrollterminForm.patchValue(patch);
    }

    // Save ALL fields in a single API call
    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, changes).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedKontrolltermin) => {
        console.log('KontrollterminEditComponent: Batch update successful:', updatedKontrolltermin);

        // Update local state for all changed fields
        if (this.kontrolltermin) {
          Object.keys(changes).forEach(key => {
            (this.kontrolltermin as any)[key] = changes[key];
          });
          if (updatedKontrolltermin.techniker) {
            this.kontrolltermin.techniker = updatedKontrolltermin.techniker;
          }
        }

        // If datum_durchfuerung or techniker_id was changed, ask about bulk-updating kontrollindikatoren
        if (changes['datum_durchfuerung'] || changes['techniker_id']) {
          this.isSaving = false;
          this.askBulkUpdateKontrollIndikatoren(changes);
        } else {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_CHANGES_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          this.isSaving = false;
        }
      },
      error: (error) => {
        console.error('KontrollterminEditComponent: Error in batch update:', error);
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_CHANGES_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.isSaving = false;
      }
    });
  }

  /**
   * After kontrolltermin is saved successfully, optionally bulk-update
   * kontrollindikatoren (checked date and/or checked_by) if relevant fields changed.
   */
  private askBulkUpdateKontrollIndikatoren(changes: { [key: string]: any }): void {
    // Build a descriptive message for the confirmation dialog
    const changedParts: string[] = [];
    let formattedDate = '';

    if (changes['datum_durchfuerung']) {
      const dateObj = new Date(changes['datum_durchfuerung']);
      formattedDate = dateObj.toLocaleDateString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
      changedParts.push(`Prüfdatum auf ${formattedDate}`);
    }
    if (changes['techniker_id']) {
      changedParts.push('Techniker');
    }

    const changedText = changedParts.join(' und ');

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Kontrollindikatoren aktualisieren?',
        message: `${changedText} wurde(n) erfolgreich geändert. Möchten Sie auch alle Kontrollindikatoren dieses Kontrolltermins entsprechend aktualisieren?`,
        confirmText: 'Ja, alle aktualisieren',
        cancelText: 'Nein',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isSaving = true;
        this.kontrollIndikatorService.bulkUpdateChecked(
          this.kontrolltermin!.id,
          changes['datum_durchfuerung'] || undefined,
          changes['techniker_id'] || undefined
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (result: any) => {
            const responseData = result.data || result;
            const updatedCount = responseData?.updated_count || 0;
            this.snackBar.open(
              `${updatedCount} ${this.translate.instant('APPOINTMENT_EDIT.SUCCESS_INDICATOR_UPDATED')}`,
              this.translate.instant('COMMON.CLOSE'), { duration: 4000 }
            );
            this.isSaving = false;
            this.loadKontrolltermin();
          },
          error: (error) => {
            console.error('KontrollterminEditComponent: Error updating kontrollindikatoren:', error);
            this.snackBar.open(
              this.translate.instant('APPOINTMENT_EDIT.ERROR_CHANGES_SAVED'),
              this.translate.instant('COMMON.CLOSE'), { duration: 4000 }
            );
            this.isSaving = false;
          }
        });
      } else {
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_CHANGES_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        this.loadKontrolltermin();
      }
    });
  }

  // Form getters
  get indicatorsFormArray(): FormArray {
    return this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
  }

  get freigabeFormGroup(): FormGroup | null {
    return this.kontrollterminForm?.get('freigabe') as FormGroup || null;
  }

  getEditableFields(): string[] {
    if (this.sourceStatus === 'bearbeitung') {
      return ['soll_zeit', 'ist_zeit'];
    } else if (this.sourceStatus === 'freigabe') {
      const editableFields = ['soll_zeit', 'ist_zeit'];
      
      // Check if user has permission to edit prüfdatum (Büro, CompAdmin, SuperAdmin)
      const role = this.authQuery?.role;
      const roleNum = typeof role === 'string' ? parseInt(role, 10) : role;
      const canEditPruefdatum = roleNum === 1 || roleNum === 2 || roleNum === 11; // SuperUser, CompAdmin, Büro
      
      if (this.isFreigabeEditMode) {
        editableFields.push('status_id', 'techniker_id');
      }
      
      // Add prüfdatum if user has permission
      if (canEditPruefdatum) {
        editableFields.push('pruefdatum');
      }
      
      return editableFields;
    }
    return [];
  }

  /**
   * Go back to kontrolltermine list
   */
  goBackToList(): void {
    this.router.navigate(['/kontrolltermine']);
  }

  setKeinBefall(indikator: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!indikator?.id) return;

    // Mark as saving on this row
    indikator._kbSaving = true;

    const session = getSession();
    // Use fid (mitarbeiter_id) first, fall back to userid (account ID)
    const userId = session?.fid || session?.userid || this.kontrolltermin?.techniker_id;

    const payload: any = {
      wert: 0,
      befall_status_id: 1, // Green = kein Befall
      befall: 0,
      anmerkung: 'Kein Befall festgestellt',
      checked: new Date().toISOString(),
      checked_by: userId ? parseInt(String(userId), 10) : null
    };

    this.kontrollIndikatorService.updateKontrollIndikator(indikator.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Update local data from server response for accuracy
          const data = response?.data || response;
          indikator.wert = data?.wert ?? 0;
          indikator.befall_status_id = data?.befall_status_id ?? 1;
          indikator.befall = data?.befall ?? 0;
          indikator.anmerkung = data?.anmerkung || 'Kein Befall festgestellt';
          indikator.checked = data?.checked || payload.checked;
          indikator.checked_by = data?.checked_by || userId;
          // Update checked_by_name from API response so the dialog can display it
          indikator.checked_by_name = data?.checked_by_name || null;
          indikator._kbSaving = false;

          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_INDICATOR_SAVED'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
        },
        error: (err) => {
          console.error('Error saving Kein Befall:', err);
          indikator._kbSaving = false;
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SAVING_SHORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      });
  }

  editIndikator(indikator: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    console.log('🔧 Opening indikator edit dialog for:', indikator);
    
    const dialogRef = this.dialog.open(DialogIndikatorEditComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        indikator: { ...indikator }, // Create a copy to avoid direct mutation
        befallAmpelOptions: this.befallAmpelOptions,
        currentStatus: this.currentStatus, // Pass current kontrolltermin status
        isViewMode: this.currentStatus === 'freigabe' || this.currentStatus === 'abgeschlossen'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('💾 Indikator updated:', result);
        // Update the indikator in the kontrolltermin object
        const index = this.kontrolltermin?.kontrollIndikatoren?.findIndex(ki => ki.id === result.id);
        if (index !== undefined && index >= 0 && this.kontrolltermin?.kontrollIndikatoren) {
          this.kontrolltermin.kontrollIndikatoren[index] = result;
          
          // Also update the form array to trigger UI refresh
          const indicatorsArray = this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
          if (indicatorsArray && indicatorsArray.at(index)) {
            indicatorsArray.at(index).patchValue(result);
          }
          
          // Trigger change detection to update the UI
          this.cdr.detectChanges();
        }
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_INDICATOR_UPDATED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  /**
   * Complete the control (Kontrolle abschließen) - set status to Freigabe
   */
  kontrolleAbschliessen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Show confirmation dialog
    this.showCompletionConfirmation();
  }

  /**
   * Show confirmation dialog for completing the control
   */
  private showCompletionConfirmation(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolle abschließen',
        message: 'Möchten Sie die Kontrolle wirklich abschließen? Der Status wird auf "Freigabe" gesetzt und der Kontrolltermin kann nicht mehr bearbeitet werden.',
        confirmText: 'Abschließen',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.processControlCompletion();
      }
    });
  }

  /**
   * Process the control completion
   */
  private processControlCompletion(): void {
    this.isSaving = true;
    
    // Update the status to Freigabe (status_id = 6)
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      status_id: 6 // Freigabe status
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrollterminId, updatedKontrolltermin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_COMPLETED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          
          // Navigate back to the list
          this.router.navigate(['/kontrolltermine']);
        },
        error: (error) => {
          this.isSaving = false;
          console.error('Error completing control:', error);
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_COMPLETING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      });
  }

  getProblemItems(indikator: any): any[] {
    try {
      if (indikator.problem) {
        // Check if it's JSON format
        if (indikator.problem.startsWith('[') || indikator.problem.startsWith('{')) {
          return JSON.parse(indikator.problem);
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.problem,
            description: 'Text input'
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing problem data:', e);
      // Return as plain text if JSON parsing fails
      return [{
        id: 'text-' + Date.now(),
        name: indikator.problem,
        description: 'Text input'
      }];
    }
    return [];
  }

  getMassnahmeItems(indikator: any): any[] {
    try {
      if (indikator.massnahme) {
        // Check if it's JSON format
        if (indikator.massnahme.startsWith('[') || indikator.massnahme.startsWith('{')) {
          return JSON.parse(indikator.massnahme);
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.massnahme,
            description: 'Text input'
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing massnahme data:', e);
      // Return as plain text if JSON parsing fails
      return [{
        id: 'text-' + Date.now(),
        name: indikator.massnahme,
        description: 'Text input'
      }];
    }
    return [];
  }

  getArtikelItems(indikator: any): ArtikelItem[] {
    try {
      if (indikator.interne_notiz) {
        // Check if it's JSON format
        if (indikator.interne_notiz.startsWith('[') || indikator.interne_notiz.startsWith('{')) {
          const parsed = JSON.parse(indikator.interne_notiz);
          if (Array.isArray(parsed)) {
            return parsed.map(item => ({
              id: item.id || 'unknown',
              name: item.name || item.toString(),
              description: item.description,
              amount: item.amount || 1 // Default to 1 if not specified
            }));
          }
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.interne_notiz,
            description: 'Text input',
            amount: 1
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing artikel data:', e);
      // Return as plain text if JSON parsing fails
      if (indikator.interne_notiz) {
        return [{
          id: 'text-' + Date.now(),
          name: indikator.interne_notiz,
          description: 'Text input',
          amount: 1
        }];
      }
    }
    return [];
  }

  /**
   * Complete the bearbeitung (Abschließen)
   */
  abschliessen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Bearbeitung abschließen',
        message: 'Möchten Sie die Bearbeitung wirklich abschließen? Der Status wird auf "Freigabe" gesetzt.',
        confirmText: 'Abschließen',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.completeBearbeitung();
      }
    });
  }

  /**
   * Complete the bearbeitung and move to freigabe status
   */
  private completeBearbeitung(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isSaving = true;

    // Get current form data
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get current kontrollIndikatoren (for preserving in local state after save)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Create updated kontrolltermin object
    // CRITICAL: Do NOT include kontrollIndikatoren in the payload - they should only be updated
    // when explicitly modifying indikatoren, not when changing status
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue,
      // Update status to freigabe (status 7)
      status_id: 7,
      // Add completion date
      completion_date_mobile: new Date().toISOString().split('T')[0],
      // Update timestamp
      updated_at: new Date().toISOString()
    };
    
    // Explicitly remove kontrollIndikatoren from payload to prevent API from recreating them
    delete updatedKontrolltermin.kontrollIndikatoren;
    delete updatedKontrolltermin.kontroll_indikatoren;

    console.log('🔄 Completing bearbeitung, updating to freigabe status...');
    console.log('📊 Updated kontrolltermin:', updatedKontrolltermin);

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSaving = false;
        console.log('✅ Bearbeitung completed successfully:', response);
        
        // Update local data
        this.kontrolltermin = {
          ...response,
          // Preserve existing kontrollIndikatoren if not in response
          kontrollIndikatoren: response.kontrollIndikatoren || currentKontrollIndikatoren
        };
        
        // Update current status
        this.currentStatus = this.getStatusKeyFromId(response.status_id);
        
        // Update form with new data
        this.kontrollterminForm.patchValue(response);
        
        // Update URL to reflect new status
        this.updateUrl();
        
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_FREIGABE_COMPLETED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        // Navigate back to kontrolltermine list
        setTimeout(() => {
          this.router.navigate(['/kontrolltermine']);
        }, 1000);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('❌ Error completing bearbeitung:', error);
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_FREIGABE_COMPLETING'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }



  getIndikatorDisplayNumber(indikator: any): string {
    const n = indikator?.indikator?.indikator_number
      ?? indikator?.indikator_nummer
      ?? indikator?.nummer
      ?? indikator?.indikator_id;
    const num = Number(n);
    if (isFinite(num)) {
      return '#' + num.toString().padStart(3, '0');
    }
    return '#—';
  }

  private sortIndicatorsByNumber(indicators: any[]): any[] {
    return [...indicators].sort((a, b) => {
      const numA = a.indikator?.indikator_number ?? a.indikator_number ?? 999999;
      const numB = b.indikator?.indikator_number ?? b.indikator_number ?? 999999;
      return numA - numB;
    });
  }

  getSymbolUrlFromIndicator(indicator: any): string | null {
    const symbolId = indicator?.indikator?.symbol_id;
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getSymbolColorFromIndicator(indicator: any): string | null {
    const symbolId = indicator?.indikator?.symbol_id;
    if (!symbolId) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[symbolId] || null;
  }

  getIndikatorCode(indicator: any): string {
    // For kontrollIndikator: kontrollIndikator.indikator = RoomIndikator, RoomIndikator.indikator = Base Indikator (with code)
    // So we need to go one level deeper: indicator.indikator.indikator.code
    return indicator?.indikator?.indikator?.code || indicator?.indikator?.code || '';
  }

  getFormattedSchwellenwert(indicator: any): string {
    const schwellenwert = indicator?.indikator?.schwellenwert;
    if (!schwellenwert) return '-';
    
    // Convert to number and format as integer
    const num = Number(schwellenwert);
    return isFinite(num) ? Math.round(num).toString() : '-';
  }

  getFormattedWert(wert: any): string {
    if (wert === null || wert === undefined || wert === '') {
      return '-';
    }
    
    // Convert to number and format as integer
    const num = Number(wert);
    return isFinite(num) ? Math.round(num).toString() : '-';
  }

  /**
   * Check if indikator was scanned via QR code
   */
  isIndikatorScanned(indikator: any): boolean {
    return !!(indikator.scanned_at);
  }

  /**
   * Get tooltip text for scanned indikator
   */
  getScanTooltipText(indikator: any): string {
    if (!indikator.scanned_at) {
      return '';
    }
    
    const scanDate = new Date(indikator.scanned_at);
    const formattedDate = scanDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = scanDate.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `Gescannt mit QR-Code am ${formattedDate} um ${formattedTime} Uhr`;
  }

  private getSchwellenwertFromIndikator(indikator: any): number {
    const sw = indikator?.indikator?.schwellenwert;
    const num = Number(sw);
    return isFinite(num) ? num : 0;
  }

  getComputedBefall(indikator: any): 0 | 1 {
    const wert = Number(indikator?.wert);
    const schwellenwert = this.getSchwellenwertFromIndikator(indikator);
    if (!isFinite(wert)) return 0;
    return wert > schwellenwert ? 1 : 0;
  }

  getComputedStatusId(indikator: any): '1' | '2' | '3' {
    const wert = Number(indikator?.wert);
    const schwellenwert = this.getSchwellenwertFromIndikator(indikator);
    if (!isFinite(wert)) return '2';
    if (wert === 0) return '3'; // green
    if (wert > schwellenwert) return '1'; // red
    return '2'; // orange
  }

  getCurrentUser(): any {
    const session = getSession();
    return session.user || null;
  }

  getCurrentUserName(): string {
    const session = getSession();
    if (!session) return 'Unbekannter Benutzer';
    
    // Try different session properties for user name
    if (session.dienstname) return session.dienstname;
    if (session.username) return session.username;
    if (session.user?.dienstname) return session.user.dienstname;
    if (session.user?.username) return session.user.username;
    if (session.user?.full_name) return session.user.full_name;
    if (session.user?.display_name) return session.user.display_name;
    
    return 'Unbekannter Benutzer';
  }

  getContactEmail(): string {
    // Use the dedicated bericht email from standort
    if (this.kontrolltermin?.standort?.bericht_email) {
      return this.kontrolltermin.standort.bericht_email;
    }
    
    // Fallback to placeholder if no bericht email is set
    if (this.kontrolltermin?.standort?.kunde?.suchbegriff) {
      return `contact@${this.kontrolltermin.standort.kunde.suchbegriff.toLowerCase().replace(/\s+/g, '')}.com`;
    }
    
    return '';
  }

  /**
   * Load saved contacts from standort
   */
  loadSavedContacts(): void {
    
    if (this.kontrolltermin?.standort?.bericht_email) {
      try {
        let contacts: ReportContact[] = [];
        
        if (Array.isArray(this.kontrolltermin.standort.bericht_email)) {
          contacts = this.kontrolltermin.standort.bericht_email;
        } else if (typeof this.kontrolltermin.standort.bericht_email === 'string') {
          // Check if it's JSON string
          if (this.kontrolltermin.standort.bericht_email.startsWith('[') || this.kontrolltermin.standort.bericht_email.startsWith('{')) {
            try {
              contacts = JSON.parse(this.kontrolltermin.standort.bericht_email);
            } catch (parseError) {
              console.error('Error parsing JSON string:', parseError);
              // Fallback to old string format
              contacts = [{
                email: this.kontrolltermin.standort.bericht_email,
                name: '',
                anrede: ''
              }];
            }
          } else {
            // Handle old string format (backward compatibility)
            contacts = [{
              email: this.kontrolltermin.standort.bericht_email,
              name: '',
              anrede: ''
            }];
          }
        }
        
        // Add selection state to each contact (all selected by default)
        this.savedContacts = contacts.map(contact => ({
          ...contact,
          selected: true
        }));
      } catch (e) {
        console.error('Error loading saved contacts:', e);
        this.savedContacts = [];
      }
    } else {
      this.savedContacts = [];
    }
  }

  /**
   * Get selected email addresses for sending
   */
  getSelectedEmailAddresses(): string[] {
    const emails: string[] = [];
    
    // Add selected saved contacts
    this.savedContacts.forEach(contact => {
      if (contact.selected && contact.email) {
        emails.push(contact.email);
      }
    });
    
    // Add additional contact if email provided
    if (this.additionalContact.email) {
      emails.push(this.additionalContact.email);
    }
    
    return emails;
  }

  /**
   * Prepare recipients data for API
   */
  private prepareRecipientsData(): any[] {
    const recipients: any[] = [];
    
    // Add selected saved contacts
    this.savedContacts.forEach(contact => {
      if (contact.selected && contact.email) {
        recipients.push({
          email: contact.email,
          name: contact.name || '',
          anrede: contact.anrede || 'Sehr geehrte Damen und Herren,'
        });
      }
    });
    
    // Add additional contact if provided
    if (this.additionalContact.email) {
      recipients.push({
        email: this.additionalContact.email,
        name: this.additionalContact.name || '',
        anrede: this.additionalContact.anrede || 'Sehr geehrte Damen und Herren,'
      });
    }
    
    return recipients;
  }

  /**
   * Send report to selected recipients
   */
  sendReportToSelected(): void {
    const recipients = this.prepareRecipientsData();
    
    if (recipients.length === 0) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SELECT_RECIPIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }
    
    
    this.isSendingReport = true;
    
    this.reportService.sendReportWithRecipients(
      this.kontrolltermin!.id, 
      recipients,
      this.emailSubject,
      this.emailMessage
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open(`${this.translate.instant('APPOINTMENT_EDIT.SUCCESS_REPORT_SENT')} (${recipients.length})`, this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent to recipients:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        console.error('Error sending report:', error);
        
        // Check if error is about inactive kontakte
        const errorMessage = error?.error?.message || error?.message || error?.error || '';
        if (errorMessage.includes('nicht aktiv') || errorMessage.includes('inactive')) {
          // Show error dialog for inactive kontakte
          this.showInactiveKontakteError(errorMessage);
        } else {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SENDING_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      }
    });
  }

  /**
   * Send report with multiple email addresses
   */
  sendReportWithAddresses(emailAddresses: string[]): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isSendingReport = true;
    
    this.reportService.sendReport(this.kontrolltermin.id, emailAddresses).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open(`${this.translate.instant('APPOINTMENT_EDIT.SUCCESS_REPORT_SENT')} (${emailAddresses.length})`, this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent to multiple recipients:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        console.error('Error sending report:', error);
        
        // Check if error is about inactive kontakte
        const errorMessage = error?.error?.message || error?.message || error?.error || '';
        if (errorMessage.includes('nicht aktiv') || errorMessage.includes('inactive')) {
          // Show error dialog for inactive kontakte
          this.showInactiveKontakteError(errorMessage);
        } else {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SENDING_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      }
    });
  }

  /**
   * Show error dialog for inactive kontakte
   */
  private showInactiveKontakteError(errorMessage: string): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'E-Mail-Versand nicht möglich',
        message: errorMessage,
        confirmText: 'OK',
        cancelText: '',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Dialog closed
    });
  }

  confirmFreigabe(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolltermin freigeben',
        message: 'Damit wird der Akt abgeschlossen und freigegeben. Möchten Sie fortfahren?',
        confirmText: 'Ja, freigeben',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.executeFreigabe();
      }
    });
  }

  executeFreigabe(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isSaving = true;
    const session = getSession();
    const freigabeDurch = session?.fid || session?.userid; // Use the correct foreign ID
    const anmerkungen = this.freigabeFormGroup?.get('anmerkungen_freigabe')?.value || '';
    const freigabeDatum = new Date().toISOString();

    // Create freigabe data
    const freigabeData = {
      kontrolltermin_id: this.kontrolltermin.id,
      freigabe_durch: freigabeDurch,
      freigegeben: 1,
      freigabe_datum: freigabeDatum,
      anmerkungen_freigabe: anmerkungen
    };


    // Create or update freigabe record
    this.kontrollterminService.createOrUpdateFreigabe(freigabeData).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (freigabeResponse) => {
        console.log('✅ Freigabe created/updated:', freigabeResponse);
        
        // Update kontrolltermin status to abgeschlossen (status_id = 8)
        const statusUpdateData = {
          status_id: 8, // abgeschlossen
          freigabe_id: freigabeResponse.id
        };

        // Chain the second request properly
        this.kontrollterminService.updateKontrolltermin(this.kontrolltermin!.id, statusUpdateData).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (updatedKontrolltermin) => {
            // Reload freigabe data to ensure saved values are displayed
            this.kontrollterminService.getFreigabeByKontrolltermin(this.kontrolltermin!.id).pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (freigabe) => {
                // Update kontrolltermin with freigabe data
                // Preserve kontrollIndikatoren to prevent them from disappearing
                this.kontrolltermin = {
                  ...updatedKontrolltermin,
                  kontrollIndikatoren: this.kontrolltermin?.kontrollIndikatoren || updatedKontrolltermin.kontrollIndikatoren || [],
                  freigabe: freigabe || undefined
                };
                
                // Update form with freigabe data
                if (freigabe && this.freigabeFormGroup) {
                  this.freigabeFormGroup.patchValue({
                    freigegeben: freigabe.freigegeben,
                    freigabe_datum: freigabe.freigabe_datum,
                    freigabe_durch: freigabe.freigabe_durch,
                    anmerkungen_freigabe: freigabe.anmerkungen_freigabe || '',
                    versand_datum: freigabe.versand_datum,
                    auswertung_link: freigabe.auswertung_link,
                    bericht_link: freigabe.bericht_link
                  });
                }
                
                this.isSaving = false;
                
                // Update the current status to reflect the change
                this.currentStatus = this.getStatusKeyFromId(updatedKontrolltermin.status_id);
                console.log('✅ Status updated to:', this.currentStatus);
                
                // Setup the new behavior for abgeschlossen status
                this.setupAbgeschlossenBehavior();
                
                this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_FREIGABE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
                console.log('✅ Kontrolltermin freigegeben successfully');
              },
              error: (freigabeError) => {
                console.error('Error loading freigabe:', freigabeError);
                // Still update kontrolltermin even if freigabe load fails
                // Preserve kontrollIndikatoren to prevent them from disappearing
                this.kontrolltermin = {
                  ...updatedKontrolltermin,
                  kontrollIndikatoren: this.kontrolltermin?.kontrollIndikatoren || updatedKontrolltermin.kontrollIndikatoren || []
                };
                this.isSaving = false;
                this.currentStatus = this.getStatusKeyFromId(updatedKontrolltermin.status_id);
                this.setupAbgeschlossenBehavior();
                this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_FREIGABE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
              }
            });
          },
          error: (error) => {
            console.error('Error updating kontrolltermin status:', error);
            this.isSaving = false;
            this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_STATUS_UPDATE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          }
        });
      },
      error: (error) => {
        console.error('Error creating/updating freigabe:', error);
        this.isSaving = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_FREIGABE_CREATE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }




  /**
   * Get category label
   */
  getCategoryLabel(category: ReportCategory): string {
    const categoryOption = this.availableReportCategories.find(c => c.value === category);
    return categoryOption?.label || category;
  }

  /**
   * Get badge classes for report category
   */
  getCategoryBadgeClass(category: ReportCategory): string {
    const badgeClasses: Record<ReportCategory, string> = {
      'standard': 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-500/50',
      'auswertungstabelle': 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-500/50',
      'systemplan': 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-900/30 dark:text-indigo-400 dark:ring-indigo-500/50',
      'objektstruktur': 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-900/30 dark:text-teal-400 dark:ring-teal-500/50',
      'besuchsbericht': 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50',
      'qr_etiketten': 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-500/50',
      'trend_analyse': 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50',
      'indikatoren_liste': 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 dark:bg-cyan-900/30 dark:text-cyan-400 dark:ring-cyan-500/50'
    };
    return badgeClasses[category] || badgeClasses['standard'];
  }

  /**
   * Get selected category option
   */
  getSelectedCategoryOption(): ReportCategoryOption | undefined {
    return this.availableReportCategories.find(c => c.value === this.selectedReportCategory);
  }

  /**
   * Toggle category dropdown
   */
  toggleCategoryDropdown(event: Event): void {
    event.stopPropagation();
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
  }

  /**
   * Select category and close dropdown
   */
  selectCategory(category: ReportCategory, event: Event): void {
    event.stopPropagation();
    const categoryOption = this.availableReportCategories.find(c => c.value === category);
    if (categoryOption && categoryOption.available) {
      this.selectedReportCategory = category;
      this.isCategoryDropdownOpen = false;
    }
  }

  /**
   * Get Besucherbericht categories (first group)
   */
  getBesucherberichtCategories(): ReportCategoryOption[] {
    return this.availableReportCategories.filter(cat => 
      ['standard', 'besuchsbericht', 'auswertungstabelle'].includes(cat.value)
    );
  }

  /**
   * Get Struktur/Pläne categories (second group)
   */
  getStrukturCategories(): ReportCategoryOption[] {
    return this.availableReportCategories.filter(cat => 
      ['objektstruktur', 'systemplan', 'qr_etiketten'].includes(cat.value)
    );
  }

  /**
   * Get Trend categories (third group)
   */
  getTrendCategories(): ReportCategoryOption[] {
    return this.availableReportCategories.filter(cat => 
      ['trend_analyse'].includes(cat.value)
    );
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close category dropdown when clicking outside
    if (this.isCategoryDropdownOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.category-dropdown-container')) {
        this.isCategoryDropdownOpen = false;
      }
    }
  }

  /**
   * Trend year changed — recalculate date range
   */
  onTrendYearChange(): void {
    this.trendDateFrom = `${this.trendSelectedYear}-01-01`;
    this.trendDateTo = `${this.trendSelectedYear}-12-31`;
  }

  /**
   * Format date string to dd.mm.yyyy
   */
  private formatDateForFilename(dateStr: string): string {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }

  /**
   * Generate report for the kontrolltermin
   */
  generateReport(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Trend analyse uses standort-level generation with extra params
    if (this.selectedReportCategory === 'trend_analyse') {
      this.generateTrendReport();
      return;
    }

    this.generateReportWithCategory(this.selectedReportCategory);
  }

  /**
   * Generate trend analysis report (Auswertung Trend, Trend Matrix, or Periodenbericht).
   * Uses the standort_id from the current kontrolltermin.
   */
  private async generateTrendReport(): Promise<void> {
    if (!this.kontrolltermin?.standort_id) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_STANDORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    const standortId = this.kontrolltermin.standort_id;
    this.isGeneratingReport = true;

    try {
      // Step 1: Fetch trend data from API
      console.log('Step 1: Fetching trend data...', this.trendDateFrom, this.trendDateTo);
      const trendData = await this.reportService.getTrendData(
        standortId, this.trendDateFrom, this.trendDateTo
      ).toPromise();

      console.log('Trend data received:', trendData);

      // Step 2: Generate charts (unless it's trend_matrix which has no charts)
      let chartImages: { [key: string]: string } = {};

      if (this.trendReportType !== 'trend_matrix' && trendData.byCode) {
        console.log('Step 2: Generating charts...');
        chartImages = await this.trendChartService.generateAllCharts(
          trendData.byCode,
          trendData.summary.current_year,
          trendData.summary.previous_year,
          this.trendReportType
        );
        console.log('Charts generated:', Object.keys(chartImages).length);
      }

      // Step 3: Send to API for PDF generation
      console.log('Step 3: Generating PDF...');
      const pdfBlob = await this.reportService.generateStandortReport(
        standortId,
        'trend_analyse',
        {
          date_from: this.trendDateFrom,
          date_to: this.trendDateTo,
          report_type: this.trendReportType,
          chart_images: chartImages
        }
      ).toPromise();

      if (!pdfBlob) return;

      this.isGeneratingReport = false;
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_TREND_GENERATED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });

      console.log('PDF generated, size:', pdfBlob.size, 'bytes');

      // Download the PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      const reportTypeNames: { [key: string]: string } = {
        'auswertung_trend': 'Auswertung-Trend',
        'trend_matrix': 'Trend-Matrix',
        'periodenbericht': 'Periodenbericht'
      };
      const typeName = reportTypeNames[this.trendReportType] || 'Trend-Analyse';
      const standortName = this.kontrolltermin?.standort_id || 'Standort';
      const fromFormatted = this.formatDateForFilename(this.trendDateFrom);
      const toFormatted = this.formatDateForFilename(this.trendDateTo);
      link.download = `${typeName}-${fromFormatted}-${toFormatted}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      this.isGeneratingReport = false;
      console.error('Error generating trend report:', error);
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_TREND_GENERATION'), this.translate.instant('COMMON.CLOSE'), { duration: 5000 });
    }
  }

  /**
   * Generate report with selected category
   */
  private generateReportWithCategory(reportCategory: ReportCategory): void {
    if (!this.kontrolltermin) {
      return;
    }

    this.isGeneratingReport = true;
    
    this.reportService.generateReport(this.kontrolltermin.id, reportCategory).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isGeneratingReport = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_REPORT_GENERATED'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        console.log('✅ Report generated:', response);
        
        // Wait a bit for the database to commit, then reload
        // Use timer to delay the reload by 1000ms to ensure database commit
        timer(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
          console.log('🔄 Reloading report versions after generation...');
          // Update report status first (this will call loadReportInfo if has_report is true)
          this.checkReportStatus();
          // Then reload all report versions
          this.loadReportVersions();
        });
      },
      error: (error) => {
        this.isGeneratingReport = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_REPORT_GENERATION'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        console.error('Error generating report:', error);
      }
    });
  }

  /**
   * View report (download)
   */
  viewReport(): void {
    if (!this.kontrolltermin || !this.reportInfo) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Try to find report in reportVersions for better filename
    const report = this.reportVersions.find(r => r.uuid === this.reportInfo?.uuid);
    this.reportService.downloadReportById(this.reportInfo.uuid, report || undefined);
  }

  /**
   * Open report in new tab
   * Uses the same logic as viewReport() but opens in new tab instead of downloading
   */
  openReportInNewTab(): void {
    if (!this.kontrolltermin || !this.reportInfo) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Use the same method as viewReport() but open in new tab
    // Try to find report in reportVersions for better filename (same as viewReport)
    const report = this.reportVersions.find(r => r.uuid === this.reportInfo?.uuid);
    
    // Use the same authenticated request as downloadReportById but open in new tab
    // Use the same endpoint format as reportService (without /v5 prefix, it's included in apiUrl)
    this.http.get(`${environment.apiUrl}/report/download/${this.reportInfo.uuid}`, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Create blob URL and open in new tab (instead of downloading)
        const blob = new Blob([response.body!], { type: response.headers.get('content-type') || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        
        // Clean up the blob URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      },
      error: (error) => {
        console.error('Error opening report in new tab:', error);
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_OPENING_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      }
    });
  }

  /**
   * Truncate filename for display (e.g., "abc....pdf")
   */
  getTruncatedFilename(filename: string, maxLength: number = 30): string {
    if (!filename || filename.length <= maxLength) {
      return filename;
    }
    
    // Extract extension
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      // No extension, just truncate
      return filename.substring(0, maxLength - 3) + '...';
    }
    
    const nameWithoutExt = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    
    // Truncate name part, keeping extension
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + '...';
    return truncatedName + extension;
  }

  /**
   * View report as HTML (for testing layout)
   */
  viewReportHtml(reportUuid?: string): void {
    const uuid = reportUuid || this.reportInfo?.uuid;
    if (!uuid) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }
    
    // Use the report service to view HTML report
    this.reportService.viewReportHtmlById(uuid);
  }

  viewReportHtmlOld(): void {
    if (!this.kontrolltermin || !this.reportInfo) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    // Use the stored report UUID to ensure we see the same content as the PDF
    const reportUrl = `${environment.serverUrl}/report/stored/${this.reportInfo.uuid}/html`;
    window.open(reportUrl, '_blank');
    this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.HTML_REPORT_OPENING'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
  }

  /**
   * Send report via email
   */
  sendReport(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    if (!this.berichtEmail) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_ENTER_EMAIL'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isSendingReport = true;
    
    this.reportService.sendReport(this.kontrolltermin.id, [this.berichtEmail]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.SUCCESS_REPORT_SENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        console.error('Error sending report:', error);
        
        // Check if error is about inactive kontakte
        const errorMessage = error?.error?.message || error?.message || error?.error || '';
        if (errorMessage.includes('nicht aktiv') || errorMessage.includes('inactive')) {
          // Show error dialog for inactive kontakte
          this.showInactiveKontakteError(errorMessage);
        } else {
          this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_SENDING_REPORT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      }
    });
  }

  /**
   * Check report status and update UI
   */
  checkReportStatus(): void {
    if (!this.kontrolltermin) return;

    this.reportService.checkReportStatus(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (status) => {
        // Handle null response
        if (!status) {
          console.log('📊 No report status found - no report generated yet');
          this.reportStatus = null;
          this.hasReportGenerated = false;
          this.hasReportSent = false;
          return;
        }
        
        this.reportStatus = status;
        this.hasReportGenerated = status.has_report || false;
        this.hasReportSent = status.has_been_sent || false;
        
        // Load detailed report info if report exists
        if (status.has_report) {
          this.loadReportInfo();
        }
        
        console.log('📊 Report status updated:', status);
      },
      error: (error) => {
        console.error('Error checking report status:', error);
      }
    });
  }

  /**
   * Load detailed report information
   */
  loadReportInfo(): void {
    if (!this.kontrolltermin) return;

    this.reportService.getReportInfo(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (info) => {
        // Handle null response
        if (!info) {
          this.reportInfo = null;
          return;
        }
        
        this.reportInfo = info;
        console.log('📋 Report info loaded:', info);
        // Don't reload report versions here - they're already loaded at line 237
        // This prevents duplicate API calls
      },
      error: (error) => {
        console.error('Error loading report info:', error);
        this.reportInfo = null;
      }
    });
  }

  /**
   * Get formatted file size
   */
  getFormattedFileSize(): string {
    return this.reportInfo ? this.reportService.formatFileSize(this.reportInfo.file_size) : '';
  }

  /**
   * Get formatted generation date
   */
  getFormattedGenerationDate(): string {
    return this.reportInfo ? this.reportService.formatDate(this.reportInfo.generated_at) : '';
  }

  /**
   * Get formatted send date
   */
  getFormattedSendDate(): string {
    return this.reportInfo && this.reportInfo.sent_at ? this.reportService.formatDate(this.reportInfo.sent_at) : '';
  }

  /**
   * Get generated by name
   */
  getGeneratedByName(): string {
    return this.reportInfo?.generated_by_name || 'Unbekannt';
  }

  /**
   * Get sent by name
   */
  getSentByName(): string {
    return this.reportInfo?.sent_by_name || 'Unbekannt';
  }

  /**
   * Get sent to emails
   */
  getSentToEmails(): string {
    return this.reportInfo?.sent_to || '';
  }

  /**
   * Load all report versions for this kontrolltermin
   */
  loadReportVersions(): void {
    if (!this.kontrolltermin) {
      return;
    }

    // Use getReports with kontrolltermin_id filter
    // kontrolltermin.id should be the UUID
    const kontrollterminId = this.kontrolltermin.id;
    
    this.reportService.getReports({
      kontrolltermin_id: kontrollterminId,
      page: 1,
      limit: 100 // Get all versions
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response.reports || response.reports.length === 0) {
          this.reportVersions = [];
          return;
        }
        
        // Sort by creation date (newest first)
        this.reportVersions = response.reports
          .sort((a, b) => {
            // Sort by created_at date descending (newest first)
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA; // Newest first
          });
        
        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading report versions:', error);
        this.reportVersions = [];
      }
    });
  }

  /**
   * Download a specific report version by UUID
   */
  downloadReportVersion(reportUuid: string): void {
    // Find the report in reportVersions to get full data for filename generation
    const report = this.reportVersions.find(r => r.uuid === reportUuid);
    this.reportService.downloadReportById(reportUuid, report);
  }

  /**
   * Format report date for display
   */
  formatReportDate(dateString: string): string {
    return this.reportService.formatDate(dateString);
  }

  /**
   * Preview email content
   */
  previewEmail(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_NO_APPOINTMENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.reportService.getEmailTemplate(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (template) => {
        const dialogRef = this.dialog.open(EmailPreviewDialogComponent, {
          width: '600px',
          data: {
            subject: template.subject,
            content: template.html || template.body // Use full HTML email if available, otherwise use formatted body
          }
        });
        
        dialogRef.afterClosed().subscribe(result => {
          if (result && result.subject) {
            // Update the email subject if it was edited in the preview
            // Note: This would need to be stored somewhere if you want to use it when sending
            console.log('Updated subject:', result.subject);
          }
        });
      },
      error: (error) => {
        this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_EMAIL_PREVIEW'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        console.error('Error loading email template:', error);
      }
    });
  }

  getFormattedSignatureDate(): string {
    if (!this.kontrolltermin?.signature_date) {
      return '-';
    }
    const date = new Date(this.kontrolltermin.signature_date);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  onSignatureImageError(event: Event): void {
    console.error('Error loading signature image:', event);
    this.snackBar.open(this.translate.instant('APPOINTMENT_EDIT.ERROR_LOADING_SIGNATURE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
  }

  /**
   * Load images for all indikatoren
   */
  private loadAllIndikatorImages(): void {
    if (!this.kontrolltermin?.kontrollIndikatoren || this.kontrolltermin.kontrollIndikatoren.length === 0) {
      return;
    }
    
    const imageLoads = this.kontrolltermin.kontrollIndikatoren.map(indikator => 
      this.dokumenteService.getDokumente('kontroll_indikator', indikator.id, 1, 100)
        .pipe(
          map(response => {
            const images = Array.isArray(response.data) ? response.data : [];
            return { indikatorId: indikator.id, images: images as FileUpload[] };
          }),
          catchError(error => {
            console.error('Error loading images for indikator:', indikator.id, error);
            return of({ indikatorId: indikator.id, images: [] });
          })
        )
    );
    
    forkJoin(imageLoads)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          results.forEach(result => {
            this.indikatorImages[result.indikatorId] = result.images;
            delete this._loadingImagesFor[result.indikatorId];
            // Don't pre-load signed URLs - load them lazily when images are displayed
            // Signed URLs will be requested when getImageUrl() is called
          });
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('📸 Error in forkJoin for images:', error);
        }
      });
  }

  /**
   * Get images for a specific indikator (lazy-loads if not already loaded)
   */
  getImagesForIndikator(indikatorId: string): FileUpload[] {
    // If images not loaded yet, load them lazily
    if (!this.indikatorImages[indikatorId] && !this._loadingImagesFor[indikatorId]) {
      this._loadingImagesFor[indikatorId] = true;
      this.loadIndikatorImages(indikatorId);
    }
    return this.indikatorImages[indikatorId] || [];
  }

  /**
   * Load images for a single indikator (lazy loading)
   */
  private loadIndikatorImages(indikatorId: string): void {
    this.dokumenteService.getDokumente('kontroll_indikator', indikatorId, 1, 100)
      .pipe(
        takeUntil(this.destroy$),
        map(response => {
          const images = Array.isArray(response.data) ? response.data : [];
          return images as FileUpload[];
        }),
        catchError(error => {
          console.error('Error loading images for indikator:', indikatorId, error);
          delete this._loadingImagesFor[indikatorId];
          return of([]);
        })
      )
      .subscribe({
        next: (images) => {
          this.indikatorImages[indikatorId] = images;
          delete this._loadingImagesFor[indikatorId];
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Request signed URL for an image (similar to plan-edit component)
   */
  private requestSignedUrl(id: number, download: boolean = false): void {
    // Prevent multiple simultaneous requests for the same ID
    if (this._pendingUrlRequests && this._pendingUrlRequests[id]) {
      return; // Request already in progress
    }
    if (!this._pendingUrlRequests) this._pendingUrlRequests = {};
    this._pendingUrlRequests[id] = true;
    
    this.dokumenteService.signDokumentUrl(id, 3600, download)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          delete this._pendingUrlRequests![id];
          if (res && res.url) {
            // Avoid double /v5 in the final URL when res.url already begins with /v5
            // environment.apiUrl typically ends with /v5
            const base = environment.apiUrl.replace(/\/$/, '').replace(/\/v5$/, '');
            const signedUrl = `${base}${res.url}`;
            this.imageUrls[id] = signedUrl;
            
            // Trigger change detection to update the template
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          delete this._pendingUrlRequests![id];
          console.error('Error getting signed URL for image:', id, error);
          // Use error placeholder instead of direct URL to avoid 401 errors
          this.imageUrls[id] = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32"%3E%3Crect width="32" height="32" fill="%23fee2e2"/%3E%3Ctext x="16" y="20" text-anchor="middle" font-size="8" fill="%23dc2626"%3E!%3C/text%3E%3C/svg%3E';
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Get signed URL for an image
   */
  getImageUrl(image: FileUpload): string {
    // Return cached URL if available
    if (this.imageUrls[image.id]) {
      return this.imageUrls[image.id];
    }
    
    // Request signed URL and cache it (will update asynchronously)
    this.requestSignedUrl(image.id, false);
    
    // Return a placeholder data URL to avoid 401 errors while signed URL is being fetched
    // This prevents the browser from trying to load an unauthorized direct URL
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="32"%3E%3Crect width="32" height="32" fill="%23f3f4f6"/%3E%3Ctext x="16" y="20" text-anchor="middle" font-size="10" fill="%239ca3af"%3E...%3C/text%3E%3C/svg%3E';
  }

  /**
   * Open image in modal/lightbox
   */
  openImageModal(image: FileUpload, indikatorId: string): void {
    const images = this.getImagesForIndikator(indikatorId);
    const currentIndex = images.findIndex(img => img.id === image.id);
    
    if (images.length === 0) {
      console.warn('No images available for indikator:', indikatorId);
      return;
    }
    
    // Ensure all signed URLs are requested (they may already be cached)
    images.forEach(img => {
      if (!this.imageUrls[img.id]) {
        this.requestSignedUrl(img.id, false);
      }
    });
    
    // Create a function that returns the cached URL or waits for it
    const getImageUrlWithFallback = (img: FileUpload): string => {
      // Return cached URL if available
      if (this.imageUrls[img.id]) {
        return this.imageUrls[img.id];
      }
      // If not cached yet, request it and return download URL as fallback
      this.requestSignedUrl(img.id, false);
      return `${environment.apiUrl}/fileupload/${img.id}/download`;
    };
    
    const dialogData: ImageModalData = {
      images: images,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      indikatorId: indikatorId,
      getImageUrl: getImageUrlWithFallback
    };
    
    const dialogRef = this.dialog.open(ImageModalComponent, {
      width: '90%',
      maxWidth: '1400px',
      height: '90%',
      maxHeight: '90vh',
      panelClass: 'image-modal-dialog',
      data: dialogData
    });
    
    dialogRef.afterClosed().subscribe(() => {
      console.log('Image modal closed');
    });
  }

  /**
   * Handle image loading error
   */
  onImageError(event: Event, image: FileUpload): void {
    console.error('Error loading image:', image.id, event);
    const img = event.target as HTMLImageElement;
    img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ccc"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EBild nicht verfügbar%3C/text%3E%3C/svg%3E';
    img.alt = 'Bild konnte nicht geladen werden';
  }

  /**
   * Toggle additional contact accordion
   */
  toggleAdditionalContactAccordion(): void {
    this.isAdditionalContactExpanded = !this.isAdditionalContactExpanded;
  }

  /**
   * Load email template and prefill email message
   */
  loadEmailTemplate(): void {
    if (!this.kontrolltermin?.id) return;

    this.reportService.getEmailTemplate(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (template) => {
        if (template) {
          console.log('📧 EMAIL TEMPLATE RECEIVED FROM BACKEND:');
          console.log('Subject:', template.subject);
          console.log('Body length:', template.body?.length);
          console.log('Body first 500 chars:', template.body?.substring(0, 500));
          console.log('Body starts with:', template.body?.substring(0, 100));
          
          // Load subject
          if (template.subject) {
            let subject = template.subject;
            
            // Replace date pattern (DD.MM.YYYY) with month name if kontrolltermin has datum_durchfuerung
            if (this.kontrolltermin?.datum_durchfuerung) {
              // Parse date string to get month
              const dateParts = this.kontrolltermin.datum_durchfuerung.split(/[-T\s]/);
              if (dateParts.length >= 2) {
                const month = parseInt(dateParts[1], 10);
                if (!isNaN(month) && month >= 1 && month <= 12) {
                  const monthNames = [
                    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                  ];
                  const monthName = monthNames[month - 1];
                  
                  // Replace date patterns like "27.12.2025" or "26.12.2025" with month name
                  subject = subject.replace(/\d{1,2}\.\d{1,2}\.\d{4}/g, monthName);
                }
              }
            }
            
            this.emailSubject = subject;
          }
          
          // Load and format body for TinyMCE
          if (template.body) {
            let bodyContent = template.body;
            console.log('📝 FRONTEND PROCESSING - Before processing:');
            console.log('Body content length:', bodyContent.length);
            console.log('Body content first 200 chars:', bodyContent.substring(0, 200));
            console.log('Body starts with <p>:', bodyContent.trim().startsWith('<p>'));
            console.log('Body contains <br>:', bodyContent.includes('<br>'));
            
            // If body already has proper HTML structure (starts with <p>), use it as-is
            // The backend already provides properly formatted HTML from TinyMCE
            if (bodyContent.trim().startsWith('<p>') || bodyContent.trim().startsWith('<div>')) {
              // Body is already HTML - just decode HTML entities and use as-is
              // No need to wrap or reformat
              bodyContent = bodyContent;
            } else if (bodyContent.includes('<br>')) {
              // Body has <br> tags but no <p> tags - convert to paragraphs
              const lines = bodyContent.split(/<br\s*\/?>/i);
              const paragraphs: string[] = [];
              
              for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed) {
                  const decoded = this.decodeHtmlEntities(trimmed);
                  paragraphs.push(`<p>${decoded}</p>`);
                } else {
                  paragraphs.push('<p><br></p>');
                }
              }
              
              bodyContent = paragraphs.join('');
            } else if (bodyContent.includes('\n')) {
              // Plain text with newlines - convert to paragraphs
              const lines = bodyContent.split(/\n/);
              const paragraphs: string[] = [];
              
              for (const line of lines) {
                const trimmed = line.trim();
                if (trimmed) {
                  paragraphs.push(`<p>${trimmed}</p>`);
                } else {
                  paragraphs.push('<p><br></p>');
                }
              }
              
              bodyContent = paragraphs.join('');
            } else {
              // Single line plain text - wrap in <p> tag
              bodyContent = `<p>${bodyContent}</p>`;
            }
            
            console.log('📝 FRONTEND PROCESSING - After processing:');
            console.log('Final body content length:', bodyContent.length);
            console.log('Final body content first 300 chars:', bodyContent.substring(0, 300));
            console.log('Final body starts with:', bodyContent.substring(0, 100));
            
            this.emailMessage = bodyContent;
          }
          console.log('✅ Email template loaded and prefilled');
        }
      },
      error: (error) => {
        console.warn('⚠️ Could not load email template:', error);
        // Don't show error to user, just use empty message
      }
    });
  }

  /**
   * Decode HTML entities in a string
   */
  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  openEingesetzteProdukteDialog(): void {
    console.log('🔘 openEingesetzteProdukteDialog called');
    console.log('📊 kontrolltermin:', this.kontrolltermin);
    
    if (!this.kontrolltermin) {
      console.error('❌ No kontrolltermin available');
      return;
    }

    console.log('📊 kontrollIndikatoren:', this.kontrolltermin.kontrollIndikatoren || this.kontrolltermin.kontroll_indikatoren);

    try {
      const dialogRef = this.dialog.open(EingesetzteProdukteDialogComponent, {
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        data: {
          kontrolltermin: this.kontrolltermin
        },
        disableClose: false
      });

      dialogRef.afterClosed().subscribe(result => {
        console.log('✅ Dialog closed:', result);
      });
    } catch (error) {
      console.error('❌ Error opening dialog:', error);
    }
  }
}
